import { useState } from 'react';
import { ChevronRight, Ellipsis, GripVertical, Plus, Trash2, Pencil } from '@/components/ui/icons';
import type { Checklist, Task } from '@shared/types';
import { newId } from '@shared/ids';
import { setChecklists } from '@/lib/actions';
import { Popover } from '@/components/ui/Popover';
import { Menu, MenuItem } from '@/components/ui/Menu';
import { PeopleMenu } from '@/components/fields/People';
import { Avatar } from '@/components/ui/Avatar';
import { UserRoundPlus } from '@/components/ui/icons';

function ChecklistBlock({ task, cl }: { task: Task; cl: Checklist }) {
  const [open, setOpen] = useState(true);
  const [draft, setDraft] = useState('');
  const [renaming, setRenaming] = useState(false);
  const save = (next: Checklist) => setChecklists(task.id, task.checklists.map((c) => (c.id === next.id ? next : c)));
  const done = cl.items.filter((i) => i.resolved).length;
  const items = [...cl.items].sort((a, b) => a.orderIndex - b.orderIndex);
  const add = () => {
    if (!draft.trim()) return;
    save({ ...cl, items: [...cl.items, { id: newId(), name: draft.trim(), resolved: false, orderIndex: cl.items.length }] });
    setDraft('');
  };
  return (
    <div className="tm-checklist">
      <div className="tm-checklist-head">
        <button className={`tm-caret ${open ? 'open' : ''}`} onClick={() => setOpen(!open)}>
          <ChevronRight size={14} />
        </button>
        {renaming ? (
          <input
            className="input"
            style={{ height: 26, maxWidth: 260 }}
            autoFocus
            defaultValue={cl.name}
            onBlur={(e) => (save({ ...cl, name: e.target.value.trim() || cl.name }), setRenaming(false))}
            onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
          />
        ) : (
          <span className="tm-checklist-title">{cl.name}</span>
        )}
        <span className="faint" style={{ fontSize: 'var(--fs-sm)' }}>
          ({done}/{cl.items.length})
        </span>
        <span className="spacer" />
        <Popover
          content={(close) => (
            <Menu>
              <MenuItem icon={<Pencil size={15} />} label="Rename" onClick={() => (setRenaming(true), close())} />
              <MenuItem
                icon={<Trash2 size={15} />}
                label="Delete checklist"
                danger
                onClick={() => {
                  setChecklists(task.id, task.checklists.filter((c) => c.id !== cl.id));
                  close();
                }}
              />
            </Menu>
          )}
        >
          <button className="icon-btn xs">
            <Ellipsis size={14} />
          </button>
        </Popover>
      </div>
      {open && (
        <div className="tm-checklist-items">
          {items.map((it) => (
            <div key={it.id} className="tm-check-item">
              <GripVertical size={13} className="tm-check-grip" />
              <input
                type="checkbox"
                className="checkbox"
                checked={it.resolved}
                onChange={() => save({ ...cl, items: cl.items.map((x) => (x.id === it.id ? { ...x, resolved: !x.resolved } : x)) })}
              />
              <input
                className={`tm-check-name ${it.resolved ? 'done' : ''}`}
                defaultValue={it.name}
                onBlur={(e) => e.target.value.trim() !== it.name && save({ ...cl, items: cl.items.map((x) => (x.id === it.id ? { ...x, name: e.target.value.trim() || it.name } : x)) })}
                onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
              />
              <Popover
                content={(close) => (
                  <PeopleMenu
                    single
                    selected={it.assigneeId ? [it.assigneeId] : []}
                    onChange={(ids) => {
                      save({ ...cl, items: cl.items.map((x) => (x.id === it.id ? { ...x, assigneeId: ids[0] ?? null } : x)) });
                      close();
                    }}
                  />
                )}
              >
                <button className="tm-check-assignee">{it.assigneeId ? <Avatar userId={it.assigneeId} size={20} /> : <UserRoundPlus size={14} />}</button>
              </Popover>
              <button className="icon-btn xs tm-check-del" onClick={() => save({ ...cl, items: cl.items.filter((x) => x.id !== it.id) })}>
                <Trash2 size={13} />
              </button>
            </div>
          ))}
          <div className="tm-check-item add">
            <Plus size={14} className="faint" />
            <input className="tm-check-name" placeholder="Add item" value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
          </div>
        </div>
      )}
    </div>
  );
}

export function ChecklistsSection({ task }: { task: Task }) {
  if (task.checklists.length === 0) return null;
  return (
    <section className="tm-section">
      <div className="tm-section-head">
        <span className="tm-section-title" style={{ marginLeft: 4 }}>
          Checklists
        </span>
      </div>
      {[...task.checklists]
        .sort((a, b) => a.orderIndex - b.orderIndex)
        .map((cl) => (
          <ChecklistBlock key={cl.id} task={task} cl={cl} />
        ))}
    </section>
  );
}
